import { useTranslation } from 'react-i18next';
import React, { useState, useCallback, useRef, useMemo, ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import cx from 'clsx';

import { IconRcBack } from '@/assets/overview';
import { IconRcAccountSuccess } from '@/assets/account';
import { Modal, Button, HelperTooltip, ModalStepProps } from '@/components';
import { CommonModalProps } from '@/components/Modal/CommonModal';
import { AccountStatus } from '@/consts';
import { useCurrentAddressInfo, useL2AccountStatus, useCurrentAddressAccount } from '@/hooks';
import {
  devLog,
  getL2SignedTransferMessage,
  numFormat,
  numSeparate,
  getL2TxRaw,
  L2TxReqParams,
} from '@/utils';
import { PasswordInput, PasswordErrorType } from '@/views/Account/components';

import style from './index.module.scss';

const { Steps } = Modal;

type PayModalProps = {
  txParams?: {
    price: number;
    message: string;
    senderAddr: string;
    receiverAddr: string;
  };
  onPaySubmit(data: { tx: L2TxReqParams }): Promise<void>;
  payTitle: ReactNode;
  warningPurpose?: string;
  isConfimLoading?: boolean;
  feeTip?: ReactNode;
} & Pick<CommonModalProps, 'size'>;

type L2PayModalProps = {
  turnOff(): void;
  // 这是第一步卡片视图，可不传
  children?: React.ReactElement<ModalStepProps<null>> | null;
  successModalSlot?: ReactNode;
  showSuccess?: boolean;
  modalClassName?: string;
} & PayModalProps;

export const L2PayModal = ({
  turnOff,
  children = null,
  warningPurpose,
  showSuccess,
  txParams,
  onPaySubmit,
  payTitle,
  successModalSlot,
  modalClassName,
  size,
  isConfimLoading,
  feeTip,
}: L2PayModalProps) => {
  const { t } = useTranslation();

  return (
    <Steps onRequestClose={turnOff} modalClassName={modalClassName} size={size}>
      {children}
      <PayModal
        warningPurpose={warningPurpose || t('L2PayModal.to-continue')}
        key="pay"
        txParams={txParams}
        onPaySubmit={onPaySubmit}
        payTitle={payTitle}
        isConfimLoading={isConfimLoading}
        feeTip={feeTip}
      />
      {showSuccess ? <SuccessModal slot={successModalSlot} /> : null}
    </Steps>
  );
};

const PayModal = ({
  ctx,
  showNext,
  showPre,
  onClose,
  warningPurpose,
  txParams,
  onPaySubmit,
  payTitle,
  currentStep,
  isConfimLoading,
  feeTip,
}: ModalStepProps<null> & PayModalProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const status = useL2AccountStatus();
  const { account: localCurrentAccountInfo } = useCurrentAddressInfo();
  const { data: myAccountInfo, ...myAccountInfoQuery } = useCurrentAddressAccount();

  const txPrice = useMemo(
    () => (txParams?.price ? numSeparate(txParams.price / 10000, 2, true) : ''),
    [txParams?.price],
  );

  const isSkipPassword = useMemo(
    () => true || (!myAccountInfoQuery.isLoading && myAccountInfo?.free_password),
    [myAccountInfo?.free_password, myAccountInfoQuery.isLoading],
  );

  const showKeyError = useMemo(
    () => !isSkipPassword && status !== AccountStatus.NORMAL,
    [isSkipPassword, status],
  );

  const [enableSkipPassword, setSkipPassword] = useState(false);

  const [isSending, setIsSending] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const [errorType, setErrorType] = useState<PasswordErrorType>();

  const onPasswordChange = useCallback(() => {
    setIsButtonDisabled(passwordInputRef?.current?.value?.length !== 6);
  }, []);

  const nextTxNonce = useMemo(() => myAccountInfo?.nonce || 0, [myAccountInfo?.nonce]);

  const txBase = useMemo(
    () =>
      txParams
        ? {
            senderAddr: txParams.senderAddr,
            receiverAddr: txParams.receiverAddr,
            price: txParams.price,
            message: txParams.message,
            nextTxNonce,
          }
        : null,
    [nextTxNonce, txParams],
  );

  const rawText = useMemo(() => (txBase ? getL2TxRaw(txBase) : ''), [txBase]);

  const insufficientBalance = useMemo(
    () => !myAccountInfo?.balance || myAccountInfo?.balance * 10000 < (txParams?.price || 0),
    [myAccountInfo?.balance, txParams?.price],
  );

  const handleSubmit = useCallback(async () => {
    const secret = passwordInputRef?.current?.value;

    if ((!secret && !isSkipPassword) || myAccountInfoQuery.isLoading || !txBase || isSending) {
      return;
    }

    setIsSending(true);
    let tx: L2TxReqParams | undefined = undefined;
    try {
      tx = getL2SignedTransferMessage({
        ...txBase,
        encryptedPrivateKey: localCurrentAccountInfo?.encryptedPrivateKey,
        secret,
        setSkipPassword: enableSkipPassword,
      });
    } catch {
      setIsSending(false);
      setErrorType(PasswordErrorType.WRONG_PASSWORD);
      if (passwordInputRef.current) {
        passwordInputRef.current.value = '';
      }
    }
    if (!tx) {
      return;
    }
    try {
      devLog('SubmitOffer', 'tx', tx);
      await onPaySubmit({ tx }).then(() => {
        setIsSending(false);

        showNext!();
      });
    } catch (e) {
      if (passwordInputRef.current) {
        passwordInputRef.current.value = '';
      }
      Sentry.captureException(e);
      setErrorType(PasswordErrorType.VERIFY_ERROR);

      setIsSending(false);
    }
  }, [
    isSkipPassword,
    myAccountInfoQuery.isLoading,
    txBase,
    localCurrentAccountInfo?.encryptedPrivateKey,
    isSending,
    enableSkipPassword,
    onPaySubmit,
    showNext,
  ]);

  return (
    <div className={style.payModal}>
      <div className={style.header}>
        {currentStep ? <IconRcBack className={style.backBtn} onClick={showPre} /> : null}
        <div>{t('L2PayModal.kttpqq')}</div>
        <HelperTooltip
          overlayClassName="offer-modal-raw-text-tooltip"
          title={rawText}
          placement={'rightTop'}>
          <div className={style.viewRaw}>{t('L2PayModal.jr23al')}</div>
        </HelperTooltip>
      </div>
      {!status || myAccountInfoQuery.isLoading ? (
        <div className={style.payBody} />
      ) : showKeyError ? (
        <div className={style.payInfo}>{payTitle}</div>
      ) : insufficientBalance ? (
        <div className={style.insufficientBalanceContainer}>
          <div className={style.insufficientBalance}>
            {t('L2PayModal.31j7iz')}{' '}
            <span className={style.balance}>{numFormat(myAccountInfo?.balance, 2, '$')}</span>
          </div>
          <div className={style.insufficientBalanceBottom}>
            ({warningPurpose} ${txPrice})
          </div>
        </div>
      ) : (
        <>
          <div className={cx(style.payInfo, isSkipPassword && style.isSkipPassword)}>
            {payTitle}
          </div>
          <div className={style.body}>
            {isSkipPassword ? null : (
              <PasswordInput
                passwordInputRef={passwordInputRef}
                onPasswordChange={onPasswordChange}
                setSkipPassword={setSkipPassword}
                showError={!!errorType}
                errorType={errorType}
              />
            )}
            <div className={style.desc}>
              {t('L2PayModal.1j0yn7m')}: {numFormat(myAccountInfo?.balance, 2, '$')}
            </div>
          </div>
        </>
      )}
      <div className={style.footer}>
        <Button type="default" className={style.footerButton} onClick={onClose}>
          {t('L2PayModal.x9d2fu')}
        </Button>
        {insufficientBalance ? (
          <Button
            type="primary"
            className={style.footerButton}
            onClick={() => history.push('/account')}>
            {t('L2PayModal.u5swep')}
          </Button>
        ) : (
          <Button
            type="primary"
            loading={isConfimLoading || isSending}
            disabled={(isButtonDisabled && !isSkipPassword) || showKeyError}
            className={style.footerButton}
            onClick={handleSubmit}>
            {t('L2PayModal.179d1nk')}
          </Button>
        )}
      </div>
      {feeTip ? <div className={style.payFee}>{feeTip}</div> : null}
    </div>
  );
};

const SuccessModal = ({
  showNext,
  ctx,
  onClose,
  slot,
}: ModalStepProps<null> & { slot: ReactNode }) => {
  const { t } = useTranslation();

  return (
    <div className={style.success}>
      <IconRcAccountSuccess className={style.successIcon} />
      {slot}
      <Button type="primary" onClick={showNext} className={style.btn}>
        {t('L2PayModal.1gppw')}
      </Button>
    </div>
  );
};
